import type { DashboardInsightTab } from '@/shared/api/modules/facilities';
import { Tabs, type TabOption } from '@/shared/ui';
import { AiInsightPanel } from '@/widgets/ai-insight-panel';
import { FacilityFloorPlanTab } from '@/widgets/facility-tabs/ui/floor-plan-tab';
import { LevelsOfCareTab } from '@/widgets/facility-tabs/ui/levels-of-care-tab';
import type { Dayjs } from 'dayjs';
import { useEffect, type FC, type ReactNode } from 'react';
import { useFacilityTabsStore } from '../model/tabs.store';
import { SingleFacilityTabId } from '../model/types';
import { FinancialTab } from './financial-tab';
import { SingleFacilityGeneralTab } from './general-tab';
import { SingleFacilityInfoTab } from './info-tab';

const portfolioTabs: TabOption[] = [
  { id: SingleFacilityTabId.General, label: 'Operational' },
  { id: SingleFacilityTabId.Financial, label: 'Financial' },
  { id: SingleFacilityTabId.LevelsOfCare, label: 'Levels of Care' },
  { id: SingleFacilityTabId.FloorPlan, label: 'Floor Plan' },
];

/**
 * Info is the only Single-Mode-only tab: it shows one facility's static
 * details, which mean nothing across a portfolio.
 *
 * Levels Of Care used to live here too, while there was no aggregate backend.
 * It now aggregates across residents org-wide, so it appears in both modes.
 */
const singleModeTabs: TabOption[] = [
  { id: SingleFacilityTabId.Info, label: 'Info' },
];

/** The only two tabs the AI Insight panel covers. */
const INSIGHT_TABS: Partial<Record<SingleFacilityTabId, DashboardInsightTab>> =
  {
    [SingleFacilityTabId.General]: 'OPERATIONAL',
    [SingleFacilityTabId.Financial]: 'FINANCIAL',
  };

type SingleFacilityTabsProps = {
  /** Omit for the portfolio (All Facilities) view. */
  facilityId?: string;
  facilityName?: string;
  dateRange?: {
    from: Dayjs;
    to: Dayjs;
  };
  /** Rendered as the General tab body in the portfolio view. */
  portfolioGeneralTab?: ReactNode;
  /**
   * The facilities table for the General tab, portfolio view only. Split out of
   * `portfolioGeneralTab` so it can render below the AI Insight panel at full
   * width — the panel must always end above the table.
   */
  portfolioGeneralTable?: ReactNode;
  /**
   * The facilities table for the Financial tab, portfolio view only. The rest
   * of that tab is identical in both modes, so only the table is injected.
   */
  portfolioFinancialTable?: ReactNode;
};

export const SingleFacilityTabs: FC<SingleFacilityTabsProps> = ({
  facilityId,
  facilityName,
  dateRange,
  portfolioGeneralTab,
  portfolioGeneralTable,
  portfolioFinancialTable,
}) => {
  // Held globally so it survives the Portfolio <-> Single Mode switch, which
  // swaps component trees and would otherwise reset to General.
  const activeTabId = useFacilityTabsStore((state) => state.activeTabId);
  const setActiveTabId = useFacilityTabsStore((state) => state.setActiveTab);

  const isSingleMode = facilityId !== undefined;
  const tabs = isSingleMode
    ? [...portfolioTabs, ...singleModeTabs]
    : portfolioTabs;

  const handleTabChange = (tab: string) => {
    setActiveTabId(tab as SingleFacilityTabId);
  };

  // Leaving Single Mode while on a Single-Mode-only tab (now just Info) would
  // leave the active tab pointing at something no longer rendered. Levels Of
  // Care no longer bounces, because it exists in both modes.
  useEffect(() => {
    const isSingleModeOnlyTab = singleModeTabs.some(
      (tab) => tab.id === activeTabId
    );

    if (!isSingleMode && isSingleModeOnlyTab) {
      setActiveTabId(SingleFacilityTabId.General);
    }
  }, [isSingleMode, activeTabId]);

  const activeDashboard = INSIGHT_TABS[activeTabId];
  const showInsightPanel = Boolean(activeDashboard && dateRange);

  // The table always renders below the panel row, full width. In Single Mode
  // there is no table — a one-row table of the facility you are already looking
  // at says nothing.
  const tableSlot =
    activeTabId === SingleFacilityTabId.General
      ? portfolioGeneralTable
      : activeTabId === SingleFacilityTabId.Financial
        ? portfolioFinancialTable
        : null;

  return (
    <div className="flex flex-col gap-3">
      {/*
       * The panel sits alongside the tab strip, not below it — its header bar is
       * level with the tabs (Figma 139-56998). So the row wraps both.
       *
       * Deliberately NOT a scroll container. `overflow-x-auto` here also makes
       * the row a scrollport on the Y axis (CSS computes the other axis from
       * `visible` to `auto`), which anchored the edge tab's `sticky` to this row
       * instead of the app's scrolling content area — so the tab never stuck and
       * scrolled away with the page. Letting the horizontal overflow bubble up
       * to that same content area keeps narrow viewports scrollable with one
       * scrollbar instead of a nested one, and lets the tab stick.
       *
       * -me-4 cancels PageLayout's right gutter so the panel sits flush with the
       * viewport edge, as drawn. The table below keeps the gutter.
       */}
      <div className="-me-4 flex min-w-0">
        {/*
         * gap-5 (20px), not gap-3, between the tab strip and the dashboard
         * body. That 20px is what makes the first row of KPI cards start
         * exactly where the AI Insight header ends, which is how Figma draws
         * it (node 139-56998) and what Anna asked for on 2026-09-21. At gap-3
         * the cards began 8px above the header's bottom edge and the two rows
         * visibly failed to line up.
         */}
        <div
          data-testid="facilities-dashboard-content"
          className="flex min-w-[900px] flex-1 flex-col gap-5"
        >
          <Tabs
            tabs={tabs}
            activeTabId={activeTabId}
            onChangeTab={handleTabChange}
          />

          {activeTabId === SingleFacilityTabId.General &&
            (facilityId !== undefined ? (
              <SingleFacilityGeneralTab
                dateRange={dateRange}
                facilityId={facilityId}
                facilityName={facilityName ?? ''}
              />
            ) : (
              portfolioGeneralTab
            ))}
          {activeTabId === SingleFacilityTabId.Financial && (
            <FinancialTab dateRange={dateRange} />
          )}
          {activeTabId === SingleFacilityTabId.LevelsOfCare && dateRange && (
            <LevelsOfCareTab dateRange={dateRange} facilityId={facilityId} />
          )}
          {activeTabId === SingleFacilityTabId.FloorPlan && (
            <FacilityFloorPlanTab facilityId={facilityId} />
          )}
          {activeTabId === SingleFacilityTabId.Info &&
            facilityId !== undefined && (
              <SingleFacilityInfoTab facilityId={facilityId} />
            )}
        </div>

        {showInsightPanel && dateRange && activeDashboard && (
          <AiInsightPanel
            dateRange={dateRange}
            activeDashboard={activeDashboard}
            scopeLabel={facilityName || 'All facilities'}
            facilitiesIds={facilityId ? [+facilityId] : undefined}
          />
        )}
      </div>

      {tableSlot}
    </div>
  );
};
